import type { ExportOption, PreviewConfig } from '@/features/DataHub/modules/Export/types';
import type { ExportSettingsType } from '@/features/DataHub/modules/Export/types/context.types';

import useUniversalPreview from './useUniversalPreview';

type UseActivePreviewParams = {
  exportOption: ExportOption;
  previewConfig: PreviewConfig | undefined;
  capturePosthogEvent: () => void;
  exportApiRequestString: string;
  exportSettings: ExportSettingsType;
};

/**
 * Single hook that orchestrates preview logic based on exportOption.id.
 * Uses the universal preview hook internally - only one query runs at a time.
 * Returns { loading, error, fileContent, downloadFile } matching the preview hook interface.
 */
function useActivePreview({
  exportOption,
  capturePosthogEvent,
  exportApiRequestString
}: UseActivePreviewParams) {
  // Main preview for the selected option
  const preview = useUniversalPreview({
    optionId: exportOption.id,
    capturePosthogEvent,
    exportApiRequestString,
    exportApiUrl: exportApiRequestString // Power Query uses this as the URL
  });

  // Special case: curl shows curl command but downloads JSON data
  // We need a second preview for the download functionality
  const jsonPreview = useUniversalPreview({
    optionId: 'json',
    enabled: exportOption.id === 'curl',
    capturePosthogEvent
  });

  // Handle curl special case
  if (exportOption.id === 'curl') {
    return {
      ...preview,
      loading: preview.loading || jsonPreview.loading,
      error: preview.error || jsonPreview.error,
      downloadFile: jsonPreview.downloadFile
    };
  }

  return preview;
}

export default useActivePreview;
